import { defineStore } from "pinia";
import { CanceledError } from "axios";

import { ModuleStatus } from "@/models/ModuleStatus";

import { pointsService } from "@/services";

import { useBrandStore } from "@/stores/brand-store";
import { useCaseStore } from "@/stores/case-store";

import { isApiError } from "@/services/Api";
import type { CheckPointData, CheckPointUpdateBody, ScorePointData } from "@/models/Point";

type Data<T> = { state: "SUCCESS" | "ERROR" | "EMPTY"; data: T[] };

interface State {
  moduleStatus: ModuleStatus;
  __checkPoints__?: Data<CheckPointData>;
  __scorePoints__?: Data<ScorePointData>;
  selectedCheckset?: string;
}

export const usePointStore = defineStore("point", {
  scope: "case",
  state: (): State => ({
    moduleStatus: ModuleStatus.Initial,
  }),
  getters: {
    checkPoints(): Data<CheckPointData> | undefined {
      if (this.__checkPoints__ == null) return;

      const { state, data } = this.__checkPoints__;
      return { state, data: data.filter((a) => a.check.type !== "aggregate") };
    },
    scorePoints(): Data<ScorePointData> | undefined {
      if (this.__scorePoints__ == null) return;

      const { state, data } = this.__scorePoints__;
      return { state, data: data.filter((a) => a.currentApplicability) };
    },
  },
  actions: {
    async load() {
      const { selectedCaseName, checksets } = useCaseStore();
      const { selectedBrand, selectedClient } = useBrandStore();

      if (selectedClient == null || selectedBrand == null || selectedCaseName == null) return;

      // No check sets means we can't load points, but we also don't want to show an error
      if (checksets.length === 0) {
        this.setStatus(ModuleStatus.Ready);
        this.pointsLoaded({ state: "EMPTY", data: [] }, { state: "EMPTY", data: [] });
        return;
      }

      if (this.selectedCheckset == null || !checksets.includes(this.selectedCheckset)) {
        this.setSelectedCheckset(checksets[0]);
      }

      const hasData = this.checkPoints?.state === "SUCCESS" && this.scorePoints?.state === "SUCCESS";
      this.setStatus(hasData ? ModuleStatus.Refreshing : ModuleStatus.Loading);

      try {
        const [checkPoints, scorePoints] = await Promise.allSettled([
          pointsService.getCheckPoints(selectedClient, selectedBrand, selectedCaseName, this.selectedCheckset!),
          pointsService.getScorePoints(selectedClient, selectedBrand, selectedCaseName, this.selectedCheckset!),
        ]);

        function mapData<T>(p: PromiseSettledResult<T[]>, fallback: T[] | undefined) {
          if (p.status === "fulfilled") return { state: "SUCCESS" as const, data: p.value };

          if (isApiError(p.reason) && p.reason.status === 204) {
            return { state: "EMPTY" as const, data: [] };
          }

          if (isApiError(p.reason) && p.reason.data instanceof CanceledError && fallback != null) {
            return { state: "SUCCESS" as const, data: fallback };
          }

          return { state: "ERROR" as const, data: [] };
        }

        this.pointsLoaded(
          mapData(checkPoints, this.__checkPoints__?.data),
          mapData(scorePoints, this.__scorePoints__?.data),
        );
        this.setStatus(ModuleStatus.Ready);
      } catch (e) {
        console.error(e);
      }
    },
    async updateCheckPoint(reference: string, fileReference: string | undefined, payload: CheckPointUpdateBody) {
      const selectedCaseName = useCaseStore().selectedCaseName;
      const { selectedBrand, selectedClient } = useBrandStore();
      if (selectedClient == null || selectedBrand == null || selectedCaseName == null) return;

      try {
        await pointsService.updateCheckPoint(
          selectedClient,
          selectedBrand,
          selectedCaseName,
          fileReference,
          reference,
          payload,
        );
      } catch (error) {
        console.error(error);
        throw error;
      } finally {
        await this.load();
      }
    },
    async updateCheckset(checkset: string) {
      this.setSelectedCheckset(checkset);
      await this.load();
    },
    // Mutations

    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    pointsLoaded(
      checkPoints: NonNullable<State["__checkPoints__"]>,
      scorePoints: NonNullable<State["__scorePoints__"]>,
    ): void {
      const SCOPE_ORDER = new Map(["case", "instance", "record"].map((k, v) => [k, v]));

      const scoreOrder = new Map(useBrandStore().viewerConfig.checks?.scoreOrder?.map((k, v) => [k, v]) ?? []);
      const checkOrder = new Map(useBrandStore().viewerConfig.checks?.checkOrder?.map((k, v) => [k, v]) ?? []);

      function sortByOrder(aOrder: number | undefined, bOrder: number | undefined) {
        // Sort by order only if one of the two has an order
        if (aOrder != null || bOrder != null) {
          // References are different, so this will never be the same number
          if (aOrder != null && bOrder != null) return aOrder - bOrder;
          else if (aOrder == null) /* B has an order. Move A to end; */ return 1;
          else if (bOrder == null) /* A has an order. Move B to end; */ return -1;
        }
      }

      // Sort Checkpoints
      checkPoints.data.sort(
        (a, b) =>
          sortByOrder(checkOrder.get(a.check.reference), checkOrder.get(b.check.reference)) ??
          a.check.shortDescription.localeCompare(b.check.shortDescription),
      );

      const checkpointReferences = new Map(checkPoints.data.map((c, i) => [c.reference, i]));

      // Sort score references to checkpoints by the new sorted order
      scorePoints.data.forEach((s) =>
        s.scorepointReferences.sort(
          (a, b) =>
            sortByOrder(
              checkpointReferences.get(a.checkpointReference),
              checkpointReferences.get(b.checkpointReference),
            ) ?? 0,
        ),
      );

      // Sort Scorepoints
      scorePoints.data.sort((a, b) => {
        const aScope = SCOPE_ORDER.get(a.scope);
        const bScope = SCOPE_ORDER.get(b.scope);

        if (aScope != null && bScope != null) {
          if (aScope !== bScope) return aScope - bScope;
        } else if (aScope == null) /*  Move to end; */ return 1;
        else if (bScope == null) /*  Move to end; */ return -1;

        return (
          sortByOrder(scoreOrder.get(a.reference), scoreOrder.get(b.reference)) ??
          a.shortDescription.localeCompare(b.shortDescription)
        );
      });

      this.__checkPoints__ = Object.freeze(checkPoints);
      this.__scorePoints__ = Object.freeze(scorePoints);
    },
    setSelectedCheckset(checkset: string | undefined): void {
      this.selectedCheckset = checkset;
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.__checkPoints__ = undefined;
      this.__scorePoints__ = undefined;
      this.selectedCheckset = undefined;
    },
  },
});
